import React, { useContext, useEffect, useState } from 'react';
import { FaCamera } from 'react-icons/fa';
import { Edit2, User, Phone, Lock, Building2, Globe, Briefcase, MapPin, Store } from 'lucide-react';
import MultiSelect from '../../Components/utils/MultiSelect';
import Dropdown from '../../Components/utils/Dropdown';
import axios from 'axios';
import { LoginContext } from '../../Contexts/LoginContext';
import { showToast } from '../../Components/utils/ShowToast';
import Loader from '../../Components/utils/Loader';
import ImageCropModal from '../../Components/utils/ImageCropModel';
import { formatFullAddress } from '../../Components/utils/formatAddress';
import countries from '../../data/countries.json';
import AddressFields, { toAddressForm, EMPTY_ADDRESS } from '../../Components/utils/AddressFields';

const EMPLOYEE_OPTIONS = [
    { value: '1-20', label: '1-20' },
    { value: '21-50', label: '21-50' },
    { value: '51-100', label: '51-100' },
    { value: '101-250', label: '101-250' },
    { value: '251-500', label: '251-500' },
    { value: '501-999', label: '501-999' },
    { value: '1000+', label: '1000+' },
];

const ORG_OPTIONS = [
    { value: 'Manufacturer', label: 'Manufacturer' },
    { value: 'Exporter', label: 'Exporter' },
    { value: 'Wholesaler', label: 'Wholesaler' },
    { value: 'Retailer', label: 'Retailer' },
    { value: 'Other', label: 'Other' },
];

const API = process.env.REACT_APP_Backend_Host_Name;

const SettingsProfile = () => {
    const { user, fetchUserData } = useContext(LoginContext);
    const [previewImage, setPreviewImage] = useState(null);
    const [profileImageFile, setProfileImageFile] = useState(null);
    const [cropModalOpen, setCropModalOpen] = useState(false);
    const [tempImage, setTempImage] = useState(null);
    const [isLoading, setIsLoading] = useState(false);

    const [profileData, setProfileData] = useState(null);
    const [contactData, setContactData] = useState(null);
    const [businessData, setBusinessData] = useState(null);
    const [passwordData, setPasswordData] = useState({ currentPassword: '', newPassword: '' });
    const [deliveryAddress, setDeliveryAddress] = useState(EMPTY_ADDRESS);
    const [pickupData, setPickupData] = useState({ sameAsDelivery: true, address: EMPTY_ADDRESS });

    const [originalProfileData, setOriginalProfileData] = useState(null);
    const [originalContactData, setOriginalContactData] = useState(null);
    const [originalBusinessData, setOriginalBusinessData] = useState(null);

    const [b2bProfile, setB2bProfile] = useState(null);
    const [b2bCategories, setB2bCategories] = useState([]);

    const [editMode, setEditMode] = useState({
        profile: false,
        password: false,
        contact: false,
        deliveryAddress: false,
        pickupAddress: false,
        business: false,
    });

    const isSeller = user?.role === 'seller' && user?.sellerId;
    const isCompanySeller = user?.sellerId?.sellerType === 'Company';

    useEffect(() => {
        if (!user) return;

        const profile = {
            username: user?.username || '',
            email: user?.email || '',
            languages: user?.languages?.split(', ') || [],
            fullName: user?.fullName || user?.sellerId?.fullName || '',
            displayName: user?.sellerId?.displayName || '',
            sellerDescription: user?.sellerId?.description || '',
        };
        setProfileData(profile);
        setOriginalProfileData(profile);

        const contact = {
            primaryPhone: user?.phone || '',
            secondaryPhone: user?.secondaryPhone || '',
        };
        setContactData(contact);
        setOriginalContactData(contact);

        setPasswordData({ currentPassword: '', newPassword: '' });
        resetAddresses();
        setPreviewImage(user?.sellerId?.profileImage || null);
        setProfileImageFile(null);

        if (isSeller && isCompanySeller) {
            fetchBusinessData();
        }
    }, [user]);

    function resetAddresses() {
        setDeliveryAddress(toAddressForm(user?.buyerAddress));
        setPickupData({
            sameAsDelivery: user?.sellerId?.pickupSameAsDelivery !== false,
            address: toAddressForm(user?.sellerId?.sellerAddress),
        });
    }

    const fetchBusinessData = async () => {
        try {
            const token = localStorage.getItem('token');
            const [b2bRes, catRes] = await Promise.all([
                axios.get(`${API}/api/v1/b2b/seller/profile`, {
                    headers: { Authorization: `Bearer ${token}` },
                }),
                axios.get(`${API}/api/v1/categories?b2b=true`),
            ]);

            const profile = b2bRes.data?.profile || null;
            setB2bProfile(profile);
            setB2bCategories(catRes.data?.categories || []);

            if (profile) {
                const isWorldwide = profile.exportMarkets?.includes('Worldwide') ?? false;
                const biz = {
                    companyName: profile.companyName || '',
                    natureOfOrg: profile.natureOfOrg || 'Manufacturer',
                    natureOfOrgOther: profile.natureOfOrgOther || '',
                    numberOfEmployees: profile.numberOfEmployees || '1-20',
                    yearFounded: profile.yearFounded || '',
                    website: profile.website || '',
                    telephone: profile.telephone || [],
                    fax: profile.fax || [],
                    productionCapacity: profile.productionCapacity || '',
                    inventoryCapacity: profile.inventoryCapacity || '',
                    productCategories: profile.productCategories || [],
                    exportMarkets: isWorldwide ? [] : (profile.exportMarkets || []),
                    exportMarketsWorldwide: isWorldwide,
                    certifications: (profile.certifications || []).map(c => c?.name).filter(Boolean),
                    businessEmail: profile.businessEmail || '',
                    businessAddress: toAddressForm(profile.businessAddress),
                };
                setBusinessData(biz);
                setOriginalBusinessData(biz);
            }
        } catch {
            // B2B profile may not exist
        }
    };

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onloadend = () => {
            const img = new Image();
            img.onload = () => {
                if (img.width === img.height) {
                    setPreviewImage(reader.result);
                    setProfileImageFile(file);
                } else {
                    setTempImage(reader.result);
                    setCropModalOpen(true);
                }
            };
            img.src = reader.result;
        };
        reader.readAsDataURL(file);
        e.target.value = null;
    };

    const handleSave = async (section) => {
        try {
            setIsLoading(true);
            const token = localStorage.getItem('token');
            let response;

            if (section === 'profile') {
                const formData = new FormData();
                formData.append('section', 'profile');
                formData.append('username', profileData.username);
                formData.append('email', profileData.email);
                formData.append('fullName', profileData.fullName);
                formData.append('languages', JSON.stringify(profileData.languages));
                if (isSeller) {
                    formData.append('displayName', profileData.displayName);
                    formData.append('sellerType', user.sellerId.sellerType);
                    formData.append('description', profileData.sellerDescription);
                    formData.append('services', JSON.stringify(user.sellerId.services || []));
                    if (isCompanySeller) {
                        formData.append('yearEstablished', user.sellerId.yearEstablished || '');
                        formData.append('noOfEmployees', user.sellerId.noOfEmployees || '');
                    }
                    if (profileImageFile) {
                        formData.append('profileImage', profileImageFile);
                    }
                }
                response = await axios.put(
                    `${API}/api/v1/auth/updateSettings`,
                    formData,
                    {
                        headers: {
                            'Content-Type': 'multipart/form-data',
                            Authorization: `Bearer ${token}`,
                        },
                    }
                );
            } else if (section === 'business') {
                const payload = {
                    companyName: businessData.companyName,
                    telephone: businessData.telephone,
                    fax: businessData.fax,
                    natureOfOrg: businessData.natureOfOrg,
                    natureOfOrgOther: businessData.natureOfOrg === 'Other' ? businessData.natureOfOrgOther : '',
                    numberOfEmployees: businessData.numberOfEmployees,
                    website: businessData.website,
                    productCategories: businessData.productCategories,
                    productionCapacity: businessData.productionCapacity,
                    inventoryCapacity: businessData.inventoryCapacity,
                    certifications: businessData.certifications.map(name => ({ name, attachments: [] })),
                    exportMarkets: businessData.exportMarketsWorldwide ? ['Worldwide'] : businessData.exportMarkets,
                    yearFounded: businessData.yearFounded ? Number(businessData.yearFounded) : undefined,
                    businessEmail: businessData.businessEmail.trim(),
                    businessAddress: businessData.businessAddress,
                };

                response = await axios.put(`${API}/api/v1/b2b/seller/profile`, payload, {
                    headers: { Authorization: `Bearer ${token}` },
                });
            } else {
                let dataToSend;
                switch (section) {
                    case 'contact':
                        dataToSend = {
                            primaryPhone: contactData.primaryPhone,
                            secondaryPhone: contactData.secondaryPhone,
                        };
                        break;
                    case 'password':
                        dataToSend = {
                            currentPassword: passwordData.currentPassword,
                            newPassword: passwordData.newPassword,
                        };
                        break;
                    case 'deliveryAddress':
                        dataToSend = { address: deliveryAddress };
                        break;
                    case 'pickupAddress':
                        dataToSend = pickupData;
                        break;
                    default:
                        break;
                }
                response = await axios.put(
                    `${API}/api/v1/auth/updateSettings`,
                    { section, data: dataToSend },
                    { headers: { Authorization: `Bearer ${token}` } }
                );
            }

            if (response.data.success) {
                await fetchUserData();
                if (section === 'business') await fetchBusinessData();
                setEditMode({ ...editMode, [section]: false });
                showToast('Updated successfully!', 'success');
            }
        } catch (error) {
            showToast(error.response?.data?.message || 'Update failed', 'error');
        } finally {
            setIsLoading(false);
        }
    };

    const handleEdit = (section) => {
        setEditMode({ ...editMode, [section]: true });
    };

    const handleCancel = (section) => {
        setEditMode({ ...editMode, [section]: false });
        switch (section) {
            case 'profile':
                setProfileData(originalProfileData);
                setPreviewImage(user?.sellerId?.profileImage || null);
                setProfileImageFile(null);
                setCropModalOpen(false);
                break;
            case 'contact':
                setContactData(originalContactData);
                break;
            case 'business':
                setBusinessData(originalBusinessData);
                break;
            case 'password':
                setPasswordData({ currentPassword: '', newPassword: '' });
                break;
            case 'deliveryAddress':
            case 'pickupAddress':
                resetAddresses();
                break;
            default:
                break;
        }
    };

    const languageOptions = [
        { value: 'Urdu', label: 'Urdu' },
        { value: 'English', label: 'English' },
        { value: 'Reading', label: 'Reading' },
        { value: 'French', label: 'French' },
        { value: 'Spanish', label: 'Spanish' },
    ];

    const b2bCategoryOptions = b2bCategories.map(c => ({ value: c.name, label: c.name }));

    const sectionIcons = { profile: User, contact: Phone, deliveryAddress: MapPin, pickupAddress: Store, password: Lock, business: Building2 };

    const renderSection = (title, subtitle, content, section) => {
        const Icon = sectionIcons[section];
        return (
            <div className="stgSection">
                <div className="stgSectionHeader">
                    <div className="stgSectionHeaderLeft">
                        {Icon && <div className="stgSectionIconWrap"><Icon size={20} strokeWidth={1.8} /></div>}
                        <div>
                            <h2 className="stgSectionTitle">{title}</h2>
                            {subtitle && <p className="stgSectionSubtitle">{subtitle}</p>}
                        </div>
                    </div>
                    <div className="stgSectionHeaderRight">
                        {!editMode[section] ? (
                            <button onClick={() => handleEdit(section)} className="stgEditBtn">
                                <Edit2 size={15} strokeWidth={2} />
                                <span>Edit</span>
                            </button>
                        ) : (
                            <div className="stgActionBtns">
                                <button onClick={() => handleCancel(section)} className="stgCancelBtn">Cancel</button>
                                <button onClick={() => handleSave(section)} className="stgSaveBtn">Save Changes</button>
                            </div>
                        )}
                    </div>
                </div>
                <div className="stgSectionBody">{content}</div>
            </div>
        );
    };

    const TagDisplay = ({ values }) => (
        <div className="stgTagContainer">
            {values?.length > 0
                ? values.map((tag, i) => <span key={i} className="stgTag">{tag}</span>)
                : <span className="stgEmptyText">None added</span>}
        </div>
    );

    const TagEditor = ({ values, onChange, placeholder, numericOnly = false }) => {
        const [input, setInput] = useState('');
        const addTag = () => {
            const trimmed = input.trim();
            if (!trimmed || values.includes(trimmed)) return;
            if (numericOnly && !/^\d+$/.test(trimmed)) return;
            onChange([...values, trimmed]);
            setInput('');
        };
        return (
            <div className="stgTagEditor">
                <div className="stgTagContainer">
                    {values.map((tag, i) => (
                        <span key={i} className="stgTag">
                            {tag}
                            <button type="button" className="stgTagRemove" onClick={() => onChange(values.filter((_, j) => j !== i))}>×</button>
                        </span>
                    ))}
                </div>
                <div className="stgTagInputRow">
                    <input
                        type="text"
                        className="stgInput"
                        value={input}
                        onChange={(e) => setInput(numericOnly ? e.target.value.replace(/[^\d]/g, '') : e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addTag(); } }}
                        placeholder={placeholder}
                    />
                    <button type="button" className="stgTagAddBtn" onClick={addTag}>Add</button>
                </div>
            </div>
        );
    };

    /* ── Profile Section ── */
    const profileContent = profileData && (
        <div className="stgFormBody">
            {isSeller && (
                <div className="stgPhotoSection">
                    <div
                        className="stgPhotoWrapper"
                        onClick={editMode.profile ? () => document.getElementById('profilePhotoInput').click() : undefined}
                    >
                        {previewImage ? (
                            <img src={previewImage} alt="Profile" className="stgPhotoImg" />
                        ) : (
                            <div className="stgPhotoPlaceholder"><span>Upload Photo</span></div>
                        )}
                        {editMode.profile && (
                            <>
                                <input type="file" id="profilePhotoInput" accept="image/*" onChange={handleImageChange} className="stgPhotoInput" />
                                <div className="stgPhotoOverlay"><FaCamera className="stgPhotoIcon" /></div>
                            </>
                        )}
                    </div>
                </div>
            )}

            <div className="stgFormGrid">
                <div className="stgFormGroup">
                    <label className="stgLabel">Full Name</label>
                    <input
                        type="text"
                        className="stgInput"
                        value={profileData.fullName}
                        onChange={(e) => setProfileData({ ...profileData, fullName: e.target.value })}
                        disabled={!editMode.profile}
                        placeholder="Full Name"
                    />
                </div>
                <div className="stgFormGroup">
                    <label className="stgLabel">Username</label>
                    <input type="text" className="stgInput stgInputLocked" value={profileData.username} disabled placeholder="Username" />
                    <span className="stgHint">Username cannot be changed</span>
                </div>
                <div className="stgFormGroup">
                    <label className="stgLabel">Email</label>
                    <div className="stgEmailWrap">
                        <input
                            type="email"
                            className="stgInput"
                            value={profileData.email}
                            onChange={(e) => setProfileData({ ...profileData, email: e.target.value })}
                            disabled={!editMode.profile}
                            placeholder="Email"
                        />
                        <span className={`stgBadge ${!user?.verified ? 'stgBadgeDanger' : ''}`}>
                            {user?.verified ? 'Verified' : 'Not Verified'}
                        </span>
                    </div>
                </div>

                {isSeller && (
                    <>
                        <div className="stgFormGroup">
                            <label className="stgLabel">Display Name</label>
                            <input
                                type="text"
                                className="stgInput"
                                value={profileData.displayName}
                                onChange={(e) => setProfileData({ ...profileData, displayName: e.target.value })}
                                disabled={!editMode.profile}
                                placeholder="Display Name"
                            />
                        </div>
                    </>
                )}

                <div className="stgFormGroup stgFormFull">
                    <label className="stgLabel">Languages</label>
                    {editMode.profile ? (
                        <MultiSelect
                            options={languageOptions}
                            value={profileData.languages}
                            onChange={(opts) => setProfileData({ ...profileData, languages: opts.map(o => o.value) })}
                            placeholder="Select languages..."
                        />
                    ) : (
                        <div className="stgTagContainer">
                            {profileData.languages.map((lang, i) => (
                                <span key={i} className="stgTag">{lang}</span>
                            ))}
                        </div>
                    )}
                </div>

                {isSeller && (
                    <>
                        <div className="stgFormGroup stgFormFull">
                            <label className="stgLabel">Seller Type</label>
                            <div className="stgStaticValue">
                                <Briefcase size={15} strokeWidth={1.5} />
                                <span>{user?.sellerId?.sellerType || 'Individual'}</span>
                            </div>
                        </div>
                        <div className="stgFormGroup stgFormFull">
                            <label className="stgLabel">Business description</label>
                            <textarea
                                className="stgInput stgTextarea"
                                value={profileData.sellerDescription}
                                onChange={(e) => setProfileData({ ...profileData, sellerDescription: e.target.value })}
                                disabled={!editMode.profile}
                                placeholder="Describe your business..."
                                maxLength="600"
                            />
                            <span className="stgCharCount">{profileData.sellerDescription.length}/600</span>
                        </div>
                    </>
                )}
            </div>
        </div>
    );

    /* ── Contact Section ── */
    const contactContent = contactData && (
        <div className="stgFormBody">
            <div className="stgFormGrid">
                <div className="stgFormGroup">
                    <label className="stgLabel">Primary Phone</label>
                    <input
                        type="tel"
                        className="stgInput"
                        value={contactData.primaryPhone}
                        onChange={(e) => setContactData({ ...contactData, primaryPhone: e.target.value })}
                        disabled={!editMode.contact}
                        placeholder="Primary Phone Number"
                    />
                </div>
                <div className="stgFormGroup">
                    <label className="stgLabel">Secondary Phone</label>
                    <input
                        type="tel"
                        className="stgInput"
                        value={contactData.secondaryPhone}
                        onChange={(e) => setContactData({ ...contactData, secondaryPhone: e.target.value })}
                        disabled={!editMode.contact}
                        placeholder="Secondary Phone"
                    />
                </div>
            </div>
        </div>
    );

    /* ── Address Sections ── */
    const deliveryAddressContent = (
        <div className="stgFormBody">
            <AddressFields value={deliveryAddress} onChange={setDeliveryAddress} disabled={!editMode.deliveryAddress} />
        </div>
    );

    const pickupAddressContent = (
        <div className="stgFormBody">
            <label className="stgCheckboxRow">
                <input
                    type="checkbox"
                    checked={pickupData.sameAsDelivery}
                    onChange={(e) => setPickupData({ ...pickupData, sameAsDelivery: e.target.checked })}
                    disabled={!editMode.pickupAddress}
                />
                Same as my delivery address
            </label>
            {pickupData.sameAsDelivery ? (
                <p className="stgStaticValue">{formatFullAddress(user?.buyerAddress) || 'Add a delivery address first'}</p>
            ) : (
                <AddressFields
                    value={pickupData.address}
                    onChange={(address) => setPickupData({ ...pickupData, address })}
                    disabled={!editMode.pickupAddress}
                />
            )}
            <p className="stgHint">Buyers see only your city and province until they pay; the full address appears on their order.</p>
        </div>
    );

    /* ── Password Section ── */
    const passwordContent = (
        <div className="stgFormBody">
            <div className="stgFormGrid">
                <div className="stgFormGroup">
                    <label className="stgLabel">Current Password</label>
                    <input
                        type="password"
                        className="stgInput"
                        value={passwordData.currentPassword}
                        onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
                        disabled={!editMode.password}
                        placeholder="Enter current password"
                    />
                </div>
                <div className="stgFormGroup">
                    <label className="stgLabel">New Password</label>
                    <input
                        type="password"
                        className="stgInput"
                        value={passwordData.newPassword}
                        onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                        disabled={!editMode.password}
                        placeholder="Enter new password"
                    />
                </div>
            </div>
        </div>
    );

    /* ── Business Section ── */
    const businessContent = businessData && (
        <div className="stgFormBody">
            <div className="stgSubSection">
                <h3 className="stgSubTitle"><Building2 size={16} /> Company Identity</h3>
                <div className="stgFormGrid">
                    <div className="stgFormGroup">
                        <label className="stgLabel">Company Name</label>
                        <input
                            type="text"
                            className="stgInput"
                            value={businessData.companyName}
                            onChange={(e) => setBusinessData({ ...businessData, companyName: e.target.value })}
                            disabled={!editMode.business}
                            placeholder="Company Name"
                        />
                    </div>
                    <div className="stgFormGroup">
                        <label className="stgLabel">Year Founded</label>
                        <input
                            type="number"
                            className="stgInput"
                            value={businessData.yearFounded}
                            onChange={(e) => setBusinessData({ ...businessData, yearFounded: e.target.value })}
                            disabled={!editMode.business}
                            placeholder="e.g. 2015"
                        />
                    </div>
                    <div className="stgFormGroup">
                        <label className="stgLabel">Nature of Organization</label>
                        {editMode.business ? (
                            <Dropdown
                                options={ORG_OPTIONS}
                                value={ORG_OPTIONS.find(o => o.value === businessData.natureOfOrg)}
                                onChange={(val) => setBusinessData({ ...businessData, natureOfOrg: val.value })}
                                placeholder="Select type"
                            />
                        ) : (
                            <input type="text" className="stgInput" value={businessData.natureOfOrg} disabled />
                        )}
                    </div>
                    {businessData.natureOfOrg === 'Other' && (
                        <div className="stgFormGroup">
                            <label className="stgLabel">Organization Type (Other)</label>
                            <input
                                type="text"
                                className="stgInput"
                                value={businessData.natureOfOrgOther}
                                onChange={(e) => setBusinessData({ ...businessData, natureOfOrgOther: e.target.value })}
                                disabled={!editMode.business}
                                placeholder="Specify type"
                            />
                        </div>
                    )}
                    <div className="stgFormGroup">
                        <label className="stgLabel">Number of Employees</label>
                        {editMode.business ? (
                            <Dropdown
                                options={EMPLOYEE_OPTIONS}
                                value={EMPLOYEE_OPTIONS.find(o => o.value === businessData.numberOfEmployees)}
                                onChange={(val) => setBusinessData({ ...businessData, numberOfEmployees: val.value })}
                                placeholder="Select range"
                            />
                        ) : (
                            <input type="text" className="stgInput" value={businessData.numberOfEmployees} disabled />
                        )}
                    </div>
                    <div className="stgFormGroup">
                        <label className="stgLabel">Website</label>
                        <input
                            type="text"
                            className="stgInput"
                            value={businessData.website}
                            onChange={(e) => setBusinessData({ ...businessData, website: e.target.value })}
                            disabled={!editMode.business}
                            placeholder="https://example.com"
                        />
                    </div>
                </div>
            </div>

            <div className="stgSubSection">
                <h3 className="stgSubTitle"><Globe size={16} /> Operations & Contact</h3>
                <div className="stgFormGrid">
                    <div className="stgFormGroup">
                        <label className="stgLabel">Telephone</label>
                        {editMode.business ? (
                            <TagEditor
                                values={businessData.telephone}
                                onChange={(v) => setBusinessData({ ...businessData, telephone: v })}
                                placeholder="Add phone number"
                                numericOnly
                            />
                        ) : (
                            <TagDisplay values={businessData.telephone} />
                        )}
                    </div>
                    <div className="stgFormGroup">
                        <label className="stgLabel">Fax</label>
                        {editMode.business ? (
                            <TagEditor
                                values={businessData.fax}
                                onChange={(v) => setBusinessData({ ...businessData, fax: v })}
                                placeholder="Add fax number"
                                numericOnly
                            />
                        ) : (
                            <TagDisplay values={businessData.fax} />
                        )}
                    </div>
                    <div className="stgFormGroup stgFormFull">
                        <label className="stgLabel">Business Email</label>
                        <input
                            type="email"
                            className="stgInput"
                            value={businessData.businessEmail}
                            onChange={(e) => setBusinessData({ ...businessData, businessEmail: e.target.value })}
                            disabled={!editMode.business}
                            placeholder="orders@yourcompany.com"
                        />
                    </div>
                </div>
            </div>

            <div className="stgSubSection">
                <h3 className="stgSubTitle"><MapPin size={16} /> Business Address</h3>
                <AddressFields
                    value={businessData.businessAddress}
                    onChange={(businessAddress) => setBusinessData({ ...businessData, businessAddress })}
                    disabled={!editMode.business}
                />
                <p className="stgHint">Leave empty to use your pickup address. Buyers see only the city and province; contact details stay private.</p>
            </div>

            <div className="stgSubSection">
                <h3 className="stgSubTitle"><Briefcase size={16} /> Capacity & Markets</h3>
                <div className="stgFormGrid">
                    <div className="stgFormGroup">
                        <label className="stgLabel">Production Capacity (monthly)</label>
                        <input
                            type="text"
                            className="stgInput"
                            value={businessData.productionCapacity}
                            onChange={(e) => setBusinessData({ ...businessData, productionCapacity: e.target.value.replace(/[^\d]/g, '') })}
                            disabled={!editMode.business}
                            placeholder="e.g. 10000"
                        />
                    </div>
                    <div className="stgFormGroup">
                        <label className="stgLabel">Inventory Capacity</label>
                        <input
                            type="text"
                            className="stgInput"
                            value={businessData.inventoryCapacity}
                            onChange={(e) => setBusinessData({ ...businessData, inventoryCapacity: e.target.value.replace(/[^\d]/g, '') })}
                            disabled={!editMode.business}
                            placeholder="e.g. 6000"
                        />
                    </div>
                    <div className="stgFormGroup stgFormFull">
                        <label className="stgLabel">Product Categories</label>
                        {editMode.business ? (
                            <MultiSelect
                                options={b2bCategoryOptions}
                                value={businessData.productCategories}
                                onChange={(opts) => setBusinessData({ ...businessData, productCategories: opts.map(o => o.label) })}
                                placeholder="Select categories"
                            />
                        ) : (
                            <TagDisplay values={businessData.productCategories} />
                        )}
                    </div>
                    <div className="stgFormGroup stgFormFull">
                        <label className="stgLabel">Export Markets</label>
                        {editMode.business ? (
                            <div className="stgExportMarketsEditor" style={{ display: 'flex', flexDirection: 'column', gap: '12px', width: '100%' }}>
                                <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', userSelect: 'none', padding: '4px 0' }}>
                                    <input
                                        type="checkbox"
                                        checked={businessData.exportMarketsWorldwide || false}
                                        onChange={(e) => {
                                            setBusinessData({
                                                ...businessData,
                                                exportMarketsWorldwide: e.target.checked,
                                                exportMarkets: []
                                            });
                                        }}
                                        style={{ width: '18px', height: '18px', accentColor: '#4CAF50', cursor: 'pointer' }}
                                    />
                                    <span style={{ fontWeight: '600', color: '#333', fontSize: '14px' }}>🌍 Worldwide (ship to all countries)</span>
                                </label>
                                {!businessData.exportMarketsWorldwide ? (
                                    <MultiSelect
                                        options={countries.map(c => ({ value: c.code, label: c.name }))}
                                        value={businessData.exportMarkets || []}
                                        onChange={(selectedOptions) => setBusinessData({
                                            ...businessData,
                                            exportMarkets: selectedOptions.map(o => o.label)
                                        })}
                                        placeholder="Search and select target countries…"
                                    />
                                ) : (
                                    <p style={{ color: '#4CAF50', fontSize: '14px', margin: '4px 0 0 0', fontWeight: '500' }}>✓ Selling to all countries worldwide</p>
                                )}
                            </div>
                        ) : (
                            <TagDisplay values={businessData.exportMarketsWorldwide ? ['Worldwide'] : businessData.exportMarkets} />
                        )}
                    </div>
                    <div className="stgFormGroup stgFormFull">
                        <label className="stgLabel">Certifications</label>
                        {editMode.business ? (
                            <TagEditor
                                values={businessData.certifications}
                                onChange={(v) => setBusinessData({ ...businessData, certifications: v })}
                                placeholder="Add certification"
                            />
                        ) : (
                            <TagDisplay values={businessData.certifications} />
                        )}
                    </div>
                </div>
            </div>
        </div>
    );

    if (!user || !profileData || !contactData) return <Loader type="simpleMax" />;

    return (
        <div className="stgPage">
            {isLoading && <Loader type="checkmate" />}
            <section className="mainSection stgPageSection">
                <div className="stgPageHeader">
                    <h1 className="stgPageTitle">Account Settings</h1>
                    <p className="stgPageSubtitle">Manage your profile, contact information, and security preferences.</p>
                </div>
                <div className="stgContainer">
                    {renderSection('My Profile', 'Personal and seller information', profileContent, 'profile')}
                    {renderSection('Contact Information', 'Phone numbers', contactContent, 'contact')}
                    {renderSection('Delivery Address', 'Where your orders are delivered; postings closest to it are shown first', deliveryAddressContent, 'deliveryAddress')}
                    {isSeller && renderSection('Pickup Address', 'Where buyers collect pickup orders and how far away you are shown', pickupAddressContent, 'pickupAddress')}
                    {isSeller && isCompanySeller && b2bProfile && (
                        renderSection('Business Details', 'B2B company profile and operations', businessContent, 'business')
                    )}
                    {renderSection('Password & Security', 'Update your password', passwordContent, 'password')}
                </div>
            </section>
            <ImageCropModal
                imageSrc={tempImage}
                open={cropModalOpen}
                onCancel={() => setCropModalOpen(false)}
                onComplete={(croppedFile) => {
                    setPreviewImage(URL.createObjectURL(croppedFile));
                    setProfileImageFile(croppedFile);
                    setCropModalOpen(false);
                }}
                aspect={1}
            />
        </div>
    );
};

export default SettingsProfile;
